import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { RootState } from '../types';

export interface Campaign {
  _id: string;
  title: string;
  description: string;
  category: string;
  subCategories: string[];
  targetAudience: {
    ageRange?: {
      min: number;
      max: number;
    };
    gender?: string[];
    locations?: string[];
  };
  budget: {
    min: number;
    max: number;
    currency: string;
  };
  requirements: {
    minFollowers?: number;
    minEngagementRate?: number;
    platforms: string[];
    contentTypes: string[];
  };
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  advertiser: {
    _id: string;
    username: string;
    companyName: string;
    companyWebsite?: string;
    profileImage: string | null;
    bio?: string;
    industry?: string[];
  };
  applications: Array<{
    influencer: {
      _id: string;
      username: string;
      profileImage: string | null;
    };
    status: 'pending' | 'accepted' | 'rejected';
    message?: string;
    appliedAt: string;
    updatedAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignState {
  campaigns: Campaign[];
  selectedCampaign: Campaign | null;
  loading: boolean;
  error: string | null;
}

const initialState: CampaignState = {
  campaigns: [],
  selectedCampaign: null,
  loading: false,
  error: null,
};

interface CampaignFilters {
  minBudget?: string;
  maxBudget?: string;
  categories?: string[];
  status?: string[];
  platforms?: string[];
  contentTypes?: string[];
}

export const fetchCampaigns = createAsyncThunk(
  'campaign/fetchCampaigns',
  async ({ cursor, filters }: { cursor: string | null; filters?: CampaignFilters } = { cursor: null }, { rejectWithValue }) => {
    try {
      console.log('Fetching campaigns with cursor:', cursor);
      const response = await api.get('/api/campaigns', {
        params: {
          cursor,
          limit: 10,
          ...filters
        }
      });
      console.log('Campaigns response:', response.data);
      return {
        cursor: response.data.pagination.nextCursor,
        campaigns: response.data.data,
        hasMore: response.data.pagination.hasMore
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCampaignById = createAsyncThunk(
  'campaigns/fetchCampaignById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/campaigns/${id}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch campaign');
    }
  }
);

export const createCampaign = createAsyncThunk(
  'campaigns/createCampaign',
  async (campaignData: Partial<Campaign>, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/campaigns', campaignData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create campaign');
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async ({ id, data }: { id: string; data: Partial<Campaign> }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/api/campaigns/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update campaign');
    }
  }
);

export const deleteCampaign = createAsyncThunk(
  'campaigns/deleteCampaign',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/api/campaigns/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete campaign');
    }
  }
);

export const applyToCampaign = createAsyncThunk(
  'campaigns/applyToCampaign',
  async ({ campaignId, proposal }: { campaignId: string; proposal: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/campaigns/${campaignId}/apply`, { proposal });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to apply to campaign');
    }
  }
);

export const campaignSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    clearSelectedCampaign: (state) => {
      state.selectedCampaign = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCampaign: (state, action: PayloadAction<Campaign | null>) => {
      state.selectedCampaign = action.payload;
    },
    clearCampaigns: (state) => {
      state.campaigns = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch campaigns
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.cursor) {
          // Append new campaigns to existing ones, avoiding duplicates
          const newCampaigns = action.payload.campaigns.filter(
            (newCampaign: Campaign) => !state.campaigns.some(
              existingCampaign => existingCampaign._id === newCampaign._id
            )
          );
          state.campaigns = [...state.campaigns, ...newCampaigns];
        } else {
          // Replace campaigns when filters change or initial load
          state.campaigns = action.payload.campaigns;
        }
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch campaigns';
      })
      // Fetch campaign by ID
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.loading = false;
        state.selectedCampaign = action.payload;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create campaign
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.loading = false;
        state.campaigns.push(action.payload);
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update campaign
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.loading = false;
        const index = state.campaigns.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
        if (state.selectedCampaign?._id === action.payload._id) {
          state.selectedCampaign = action.payload;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete campaign
      .addCase(deleteCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCampaign.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(c => c._id !== action.payload);
        if (state.selectedCampaign?._id === action.payload) {
          state.selectedCampaign = null;
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Apply to campaign
      .addCase(applyToCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyToCampaign.fulfilled, (state, action: PayloadAction<Campaign['applications'][0]>) => {
        state.loading = false;
        if (state.selectedCampaign) {
          state.selectedCampaign.applications.push(action.payload);
        }
      })
      .addCase(applyToCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedCampaign, clearError } = campaignSlice.actions;

export const selectCampaigns = (state: RootState) => state.campaign.campaigns;
export const selectSelectedCampaign = (state: RootState) => state.campaign.selectedCampaign;
export const selectCampaignsLoading = (state: RootState) => state.campaign.loading;
export const selectCampaignsError = (state: RootState) => state.campaign.error;

export default campaignSlice.reducer; 